import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSessionStorage from "./useSessionStorage";

import "./Scoreboard.css"; // Import the CSS file
import useAxios from "./useAxios";

const Tile = ({ title, text, onclick }) => (
  <a onClick={onclick}>
    <div className="tile">
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  </a>
);

const Scoreboard = ({ logout }) => {
  const debug = false;
  const { id, matchid } = useParams();
  const [table, setTable] = useState(null);
  const [match, setMatch] = useState(null);
  const [streamLoading, setStreamLoading] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stream, setStream] = useState(null);
  const [token] = useSessionStorage("token", null);
  const [showBreakDialog, setShowBreakDialog] = useState(false);
  const [currentBreak, setCurrentBreak] = useState(null); // 'home' or 'away'
  const [streamWarning, setStreamWarning] = useState(null);

  const [axios] = useAxios(true);
  const navigate = useNavigate();

  // Function to get user type from token
  const getUserType = (token) => {
    if (!token) return "user";
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.user_type || "user";
    } catch (error) {
      console.error("Error decoding token:", error);
      return "user";
    }
  };

  // Function to get match ID from token for match users
  const getMatchIdFromToken = (token) => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.match_id || null;
    } catch (error) {
      console.error("Error decoding match ID from token:", error);
      return null;
    }
  };

  const log = (...messages) => {
    if (debug === false) {
      return;
    }

    setLogs((prevLogs) => {
      const newLogs = [...prevLogs, messages.join(" ")];
      return newLogs.slice(-50); // Keep only the last 10
    });
  };

  const getTable = async (table) => {
    setMatch(null);
    setStream(null);

    let apiEndpoint = "table/" + table + "/match";

    // If matchid is provided in URL, use the specific match endpoint
    if (matchid) {
      apiEndpoint = "table/" + table + "/match/" + matchid;
    } else {
      // Fallback: If user is a match-user, use the specific match endpoint
      const userType = getUserType(token);
      if (userType === "match-user") {
        const matchId = getMatchIdFromToken(token);
        if (matchId) {
          apiEndpoint = "table/" + table + "/match/" + matchId;
        }
      }
    }

    axios.get(apiEndpoint, (data) => {
      setTable(data);
      if (data.match) {
        getMatch(data.match.id);
      }
    });
  };

  const getMatch = async (matchId) => {
    axios.get("match/" + matchId, (response) => {
      setMatch(response);
      getStreamByMatch(response.id);
    });
  };

  const getStreamByMatch = async (matchId) => {
    axios.get("stream/match/" + matchId, setStream);
  };

  const setSide = async (side, value) => {
    axios.post("matchside/" + side.id, { score: value }, (data) =>
      getMatch(table.match.id)
    );
  };

  const setScore = (side, delta) => {
    setSide(match[side], match[side].score + delta);
    // Switch break to the other player when score is updated
    if (currentBreak) {
      setCurrentBreak(currentBreak === "home" ? "away" : "home");
    }
  };

  const handleBreakSelection = (selectedSide) => {
    setCurrentBreak(selectedSide);
    setShowBreakDialog(false);
  };

  const startMatch = async () => {
    setLogs([]);

    // Show loading message if match is streamed
    if (match.isStreamed) {
      setStreamLoading({
        step: 0,
        of: 1,
        text: "Starting match and creating stream..."
      });
    }

    axios.post("match/" + match.id + "/start", {}, async (response) => {
      setStreamLoading(null);
      setMatch(response);

      // If streamed, check InUse flag and get stream info
      if (response.isStreamed) {
        checkStreamInUse(response.table.id);

        if (response.streamId && response.broadcastId) {
          getStreamByMatch(response.id);
        }
      }

      // After match is started, ask who's breaking
      setShowBreakDialog(true);
    });
  };

  const checkStreamInUse = (tableId) => {
    const startTime = Date.now();
    const maxWaitTime = 2 * 60 * 1000; // 2 minutes
    let warningShown = false;

    const checkInterval = setInterval(() => {
      axios.get("table/" + tableId, (tableData) => {
        const elapsed = Date.now() - startTime;

        if (tableData.inUse) {
          // Stream started successfully
          setStreamWarning(null);
          clearInterval(checkInterval);
        } else if (elapsed >= maxWaitTime && !warningShown) {
          // 2 minutes passed and stream not started
          setStreamWarning("Warning: Stream has not started automatically. The OBS service may not be running or the stream settings may be incorrect. You can continue using the scoreboard.");
          warningShown = true;
          clearInterval(checkInterval);
        }
      });
    }, 5000); // Check every 5 seconds

    // Clear interval after 2.5 minutes to be safe
    setTimeout(() => {
      clearInterval(checkInterval);
    }, maxWaitTime + 30000);
  };

  const endStream = async (settings, stream) => {
    setStreamLoading({ step: 0, of: 1, text: "Please wait, ending stream..." });

    try {
      // Use backend API to transition broadcast to complete (headless with refresh token)
      await new Promise((resolve, reject) => {
        axios.post("youtube/transition-broadcast", {
          streamTypeId: settings.id,
          broadcastId: stream.broadcastId,
          status: "complete"
        }, resolve, reject);
      });

      setStreamLoading((x) => ({ ...x, step: 1, text: "Broadcast ended, stream will stop automatically via OBS worker..." }));

      // The OBS worker will handle stopping the stream via the scheduled task
      // created by the Match End endpoint
    } catch (err) {
      log("Stream ending failed:", err);
    }
  };

  const handleStart = () => {
    // Start the match, break dialog will appear after API returns
    startMatch();
  };

  const handleEnd = async () => {
    setLogs([]);
    const userType = getUserType(token);

    axios.post("match/" + match.id + "/end", {}, async (response) => {
      if (stream) {
        await endStream(match.table.streamType, stream);
      }

      setStreamLoading(null);

      // If user is a match user, log them out after ending the match
      if (userType === "match-user" && logout) {
        logout();
      } else {
        getTable(id);
      }
    });
  };

  useEffect(() => {
    getTable(id);
  }, []);

  // Check if we need to ask for break selection after page load
  useEffect(() => {
    if (match && match.status === 1 && !currentBreak) {
      // Match is started but we don't know who's break it is
      setShowBreakDialog(true);
    }
  }, [match, currentBreak]);

  useEffect(() => {
    const matchIntervalId = setInterval(() => {
      if (table !== null && table.match !== null) {
        getMatch(table.match.id);
      }
    }, 10000);

    // Clean up the interval when the component is unmounted
    return () => {
      clearInterval(matchIntervalId);
    };
  }, [table]);

  if (table === null) {
    return <div>Loading...</div>;
  }

  if (match === null) {
    return (
      <div className="scoreboard-container">
        <div className="scoreboard-create">
          <Tile
            title="Create Match"
            text="Create"
            onclick={() => navigate("/match/new/?table=" + table.id)}
          />
        </div>
      </div>
    );
  }

  if (streamLoading !== null) {
    return (
      <div className="spinner-overlay">
        <div className="spinner"></div>
        <h3>{streamLoading.text}</h3>
        <div className="progress-bar">
          {[...Array(streamLoading.of)].map((_, idx) => (
            <div
              key={idx}
              className={
                "progress-step " + (streamLoading.step < idx ? "filled" : "")
              }
            ></div>
          ))}
        </div>

        {debug && logs.length > 0 ? (
          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              fontFamily: "monospace",
              border: "1px solid #333",
              maxWidth: "500px",
              backgroundColor: "white",
              overflowX: "auto",
            }}
          >
            {logs.map((entry, index) => (
              <div key={index}>{entry}</div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  const userType = getUserType(token);
  const isMatchUser = userType === "match-user";

  return (
    <div className={`scoreboard-container ${isMatchUser ? 'match-user-mode' : ''}`}>
      {match.status === 0 ? (
        <button className="scoreboard-container-action" onClick={handleStart}>
          Start
        </button>
      ) : null}
      {match.raceTo <= match.home.score || match.raceTo <= match.away.score ? (
        <button className="scoreboard-container-action" onClick={handleEnd}>
          End
        </button>
      ) : null}
      <div className="scoreboard-scores">
        <div
          className={
            "scoreboard-score" + (currentBreak === "home" ? " break" : "")
          }
        >
          <h3>
            {match.home.player.name}
            {match.home.doubles === null ? "" : " & " + match.home.doubles.name}
          </h3>
          <h1 className="score">{match.home.score}</h1>
          <div>
            <button
              disabled={match.status !== 1}
              onClick={() => setScore("home", +1)}
            >
              +
            </button>
            <button
              disabled={match.status !== 1}
              onClick={() => setScore("home", -1)}
            >
              -
            </button>
          </div>
        </div>
        <div
          className={
            "scoreboard-score" + (currentBreak === "away" ? " break" : "")
          }
        >
          <h3>
            {match.away.player.name}
            {match.away.doubles === null ? "" : " & " + match.away.doubles.name}
          </h3>
          <h1 className="score">{match.away.score}</h1>
          <button
            disabled={match.status !== 1}
            onClick={() => setScore("away", +1)}
          >
            +
          </button>
          <button
            disabled={match.status !== 1}
            onClick={() => setScore("away", -1)}
          >
            -
          </button>
        </div>
      </div>

      {/* Stream Warning */}
      {streamWarning && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ff9800",
            color: "white",
            padding: "15px 30px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            zIndex: 1000,
            maxWidth: "80%",
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0, fontSize: "14px" }}>{streamWarning}</p>
          <button
            onClick={() => setStreamWarning(null)}
            style={{
              marginTop: "10px",
              padding: "5px 15px",
              backgroundColor: "white",
              color: "#ff9800",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Break Selection Dialog */}
      {showBreakDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "10px",
              textAlign: "center",
              minWidth: "300px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3 style={{ marginBottom: "20px" }}>Who's break is it?</h3>
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                gap: "20px",
              }}
            >
              <button
                onClick={() => handleBreakSelection("home")}
                style={{
                  padding: "15px 25px",
                  fontSize: "16px",
                  backgroundColor: "#1a7e59",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#22aa78")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#1a7e59")
                }
              >
                {match.home.player.name}
              </button>
              <button
                onClick={() => handleBreakSelection("away")}
                style={{
                  padding: "15px 25px",
                  fontSize: "16px",
                  backgroundColor: "#1a7e59",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#22aa78")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#1a7e59")
                }
              >
                {match.away.player.name}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scoreboard;
